<template>
  <div class="dl-wrap">
    <el-popover
      v-model="popoverVisible"
      placement="bottom-end"
      width="320"
      trigger="click"
      popper-class="dl-popover"
    >
      <!-- Trigger: icono de descarga -->
      <div slot="reference" class="dl-trigger" title="Descarga en lote">
        <i class="el-icon-download"></i>
      </div>

      <!-- Contenido del popover -->
      <div class="dl-content">
        <div class="dl-header">
          <span class="dl-title">Descarga en lote</span>
          <button class="dl-close" @click="popoverVisible = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- Selector de formato -->
        <div class="dl-field">
          <label class="dl-label">Formato</label>
          <div class="dl-format-pills">
            <button
              class="dl-format-pill"
              :class="{ active: format === 'xml' }"
              @click="format = 'xml'"
            >
              <i class="el-icon-document"></i> XML
            </button>
            <button
              class="dl-format-pill"
              :class="{ active: format === 'pdf' }"
              @click="format = 'pdf'"
            >
              <i class="el-icon-picture-outline"></i> PDF
            </button>
          </div>
        </div>

        <!-- Selector de rango de fechas -->
        <div class="dl-field">
          <label class="dl-label"
            >Rango de fechas <span class="dl-hint">(máx. 31 días)</span></label
          >
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-dd"
            range-separator="→"
            start-placeholder="Desde"
            end-placeholder="Hasta"
            style="width: 100%"
            size="small"
          />
        </div>

        <!-- Acciones -->
        <div class="dl-actions">
          <button class="dl-btn-link" @click="reset">Limpiar</button>
          <button
            class="dl-btn-primary"
            :disabled="!dateRange"
            @click="handleDownload"
          >
            <i class="el-icon-download"></i>
            Descargar
          </button>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script>
import Api from "@/config/Api";
import { signingEventBus } from "@/services/signingEventBus";

export default {
  name: "DownloadLoteComponent",

  props: {
    docType: {
      type: String,
      default: "invoice" // invoice | credit-note | debit-note | withholding
    }
  },

  data() {
    return {
      popoverVisible: false,
      format: "xml",
      dateRange: null
    };
  },

  computed: {
    userBusiness() {
      return this.$store.getters.getUser?.user_business;
    },
    docTypeLabel() {
      const labels = {
        invoice: "Facturas",
        "credit-note": "NotasCredito",
        "debit-note": "NotasDebito",
        withholding: "Retenciones"
      };
      return labels[this.docType] || "Documentos";
    },
    downloadId() {
      return `download_lote_${this.docType}`;
    }
  },

  methods: {
    reset() {
      this.dateRange = null;
      this.format = "xml";
    },

    validateRange() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        this.$notifications.warning({
          message: "Selecciona un rango de fechas"
        });
        return false;
      }
      const from = new Date(this.dateRange[0]);
      const to = new Date(this.dateRange[1]);
      const diffDays = (to - from) / (1000 * 60 * 60 * 24);
      if (diffDays > 31) {
        this.$notifications.warning({
          message: "El rango no puede superar 31 días. Elige un período menor."
        });
        return false;
      }
      return true;
    },

    handleDownload() {
      if (!this.validateRange()) return;

      const id = this.downloadId;
      const ext = this.format.toUpperCase();
      const dates = `${this.dateRange[0]}_${this.dateRange[1]}`;
      const filename = `${this.docTypeLabel}_${ext}_${dates}.zip`;
      const payload = {
        doc_type: this.docType,
        format: this.format,
        date_from: this.dateRange[0],
        date_to: this.dateRange[1],
        business_id: this.userBusiness?.id
      };

      // Cerrar popover y liberar el componente de inmediato
      this.popoverVisible = false;
      this.reset();

      // Mostrar toast activo en la parte inferior
      signingEventBus.$emit("download:start", { id, filename, format: ext });

      // ── Fire and forget ───────────────────────────────────────────────
      // El request corre en segundo plano. El usuario puede navegar libremente.
      Api()
        .post("/batch-download", payload, {
          responseType: "blob",
          skipErrorNotification: true
        })
        .then(response => {
          // Disparar descarga nativa del navegador
          const blob = new Blob([response.data], { type: "application/zip" });
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(url);
          signingEventBus.$emit("download:done", { id, filename });
        })
        .catch(err => {
          // Leer el mensaje JSON del blob de error de forma async
          const extractMsg = async () => {
            let msg = "No se pudieron obtener documentos en ese rango";
            if (err?.response?.data instanceof Blob) {
              try {
                const text = await err.response.data.text();
                const json = JSON.parse(text);
                if (json?.message) msg = json.message;
              } catch {
                /* silenciar */
              }
            }
            signingEventBus.$emit("download:error", { id, message: msg });
          };
          extractMsg();
        });
    }
  }
};
</script>

<style lang="scss">
// Non-scoped: aplica al popper que se renderiza fuera del árbol del componente
.dl-popover.el-popover {
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid #e4eaf6;
  box-shadow: 0 8px 30px rgba(45, 43, 85, 0.12);

  .el-range-separator {
    width: auto;
    min-width: 20px;
    padding: 0 2px;
    flex-shrink: 0;
    color: #909399;
    line-height: 26px;
  }
}
</style>

<style lang="scss" scoped>
$dark: #2d2b55;
$teal: #3ecfb4;
$grey: #909399;
$border: #e4eaf6;

// ── Trigger ──────────────────────────────────────────────
.dl-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid $border;
  background: #fff;
  cursor: pointer;
  color: #6b7280;
  font-size: 15px;
  transition: all 0.15s;

  &:hover {
    border-color: #c8c5d0;
    background: #f5f7fb;
    color: $dark;
  }
}

// ── Popover content ──────────────────────────────────────
.dl-content {
  padding: 4px 0;
}

.dl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.dl-title {
  font-size: 14px;
  font-weight: 600;
  color: $dark;
}

.dl-close {
  background: none;
  border: none;
  cursor: pointer;
  color: $grey;
  font-size: 14px;
  padding: 2px;
  line-height: 1;

  &:hover {
    color: $dark;
  }
}

.dl-field {
  margin-bottom: 14px;
}

.dl-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: $grey;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.dl-hint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 10px;
}

// ── Format pills ─────────────────────────────────────────
.dl-format-pills {
  display: flex;
  gap: 6px;
}

.dl-format-pill {
  flex: 1;
  padding: 6px 10px;
  border: 1.5px solid $border;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: $grey;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    border-color: $teal;
    color: $dark;
  }

  &.active {
    background: $dark;
    border-color: $dark;
    color: #fff;
  }
}

// ── Actions ──────────────────────────────────────────────
.dl-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.dl-btn-link {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: $grey;
  padding: 0 4px;

  &:hover {
    color: $dark;
  }
}

.dl-btn-primary {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 16px;
  background: $dark;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #4340a0;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}
</style>
